import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnInit,
	ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { verifyProperColumns } from '@core/util/csv';
import { ICON } from '@core/util/icon';
import {
	SolunoActivityCode,
	SolunoActivityCodeTypes,
	SolunoGLAccount,
} from '@models/soluno';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessDispatch, BusinessState } from '@state/business';
import csv from 'csvtojson';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { DropdownOption } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DropzoneComponent } from 'src/app/shared/atomic/atoms/dropzone/dropzone.component';
import { SearchableDropdownItems } from 'src/app/shared/atomic/atoms/searchable-dropdown/searchable-dropdown.types';

@Component({
	selector: 'app-soluno-import-modal',
	templateUrl: './soluno-import-modal.component.html',
	styleUrls: ['./soluno-import-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SolunoImportModalComponent implements OnInit {
	public displayedColumns: string[] = [
		'id',
		'description',
		'type',
		'GLAccount',
	];

	public activityCodes: SolunoActivityCode[] = [];

	public glAccounts: SolunoGLAccount[] = [];

	public glAccountsDropdownOptions: SearchableDropdownItems[] = [];

	public activityCodeTypesDropdownOptions: DropdownOption<SolunoActivityCodeTypes>[] =
		[
			{ name: 'Fee', value: SolunoActivityCodeTypes.FEE },
			{ name: 'Disbursement', value: SolunoActivityCodeTypes.DISBURSEMENT },
			{
				name: 'Fee and Disbursement',
				value: SolunoActivityCodeTypes.FEE_AND_DISBURSEMENT,
			},
		];

	public activityCodeTypes = SolunoActivityCodeTypes;

	public ICON = ICON;

	public glAccountFileName = '';
	public activityCodeFileName = '';

	@Select(BusinessState.getSolunoActivityCodes) activityCodes$: Observable<
		SolunoActivityCode[]
	>;
	@Select(BusinessState.getSolunoGLAccounts) glAccounts$: Observable<
		SolunoGLAccount[]
	>;
	@Select(BusinessState.getSolunoGLAccountImportedFileName)
	glAccountFileName$: Observable<string>;
	@Select(BusinessState.getSolunoActivityCodeImportedFileName)
	activityCodeFileName$: Observable<string>;

	@ViewChild('activityCodeDropzone', { static: false })
	activityCodeFileInput: DropzoneComponent;
	@ViewChild('glAccountDropzone', { static: false })
	glAccountFileInput: DropzoneComponent;

	constructor(
		private businessDispatch: BusinessDispatch,
		private matdialogRef: MatDialogRef<SolunoImportModalComponent>,
		private snackbarService: SnackbarService,
		private cdr: ChangeDetectorRef,
		private analyticsService: AnalyticsService,
	) {}

	ngOnInit() {
		this.activityCodes$
			.pipe(take(1))
			.subscribe((activityCodes: SolunoActivityCode[]) => {
				this.activityCodes = cloneDeep(activityCodes);
			});

		this.glAccounts$
			.pipe(take(1))
			.subscribe((glAccounts: SolunoGLAccount[]) => {
				this.glAccounts = cloneDeep(glAccounts);
				this.updateGLAccountDropdownOptions();
			});

		this.glAccountFileName$
			.pipe(take(1))
			.subscribe((glAccountFileName: string) => {
				this.glAccountFileName = glAccountFileName;
			});

		this.activityCodeFileName$
			.pipe(take(1))
			.subscribe((activityCodeFileName: string) => {
				this.activityCodeFileName = activityCodeFileName;
			});
	}

	async onActivityCodeFileSelected(event: File) {
		const body = verifyProperColumns(await event.text());
		csv({
			noheader: false,
			headers: ['id', 'description', ''],
			checkColumn: true,
		})
			.fromString(body)
			.subscribe(null, () => {
				this.snackbarService.openSnackBar({
					text: 'Error: Incompatible CSV file format',
					type: 'error',
				});
				// Forcing the input file name to go back to the default value
				this.activityCodeFileName = this.activityCodeFileName.trim() + ' ';
				this.cdr.detectChanges();
			})

			.then((result: SolunoActivityCode[]) => {
				this.analyticsService.logEvent(
					AnalyticsEvent.SOLUNO_BUSINESS_SETTINGS_IMPORT_ACTIVITY_CODES,
				);

				this.activityCodes = result.map((activityCode: SolunoActivityCode) => {
					const existingActivityCode = this.activityCodes.find(
						(existingActivityCode: SolunoActivityCode) => {
							return existingActivityCode.id === activityCode.id;
						},
					);

					if (!existingActivityCode) {
						return {
							...activityCode,
							type: SolunoActivityCodeTypes.FEE,
						};
					}

					return {
						...activityCode,
						type: existingActivityCode.type,
						GLAccount: existingActivityCode.GLAccount ?? null,
					};
				});

				this.activityCodeFileName = event.name;

				this.cdr.detectChanges();
			});
	}

	async onGLAccountFileSelected(event: File) {
		const body = verifyProperColumns(await event.text());
		csv({
			noheader: false,
			checkColumn: true,
			headers: [
				'id',
				'accountDescription',
				'department',
				'category',
				'costCenter',
				'accountType',
				'accountUsageType',
				'showInHelp',
				'',
			],
		})
			.fromString(body)
			.subscribe(null, () => {
				this.snackbarService.openSnackBar({
					text: 'Error: Incompatible CSV file format',
					type: 'error',
				});
				this.glAccountFileName = this.glAccountFileName.trim() + ' ';
				this.cdr.detectChanges();
			})
			.then((result: SolunoGLAccount[]) => {
				this.analyticsService.logEvent(
					AnalyticsEvent.SOLUNO_BUSINESS_SETTINGS_IMPORT_GL_CODES,
				);

				this.glAccounts = result.map((glAccount: SolunoGLAccount) => {
					const activityCodesWithMathchingGLAccount = this.activityCodes.filter(
						(activityCode: SolunoActivityCode) => {
							return activityCode.GLAccount?.id === glAccount.id;
						},
					);

					activityCodesWithMathchingGLAccount.forEach(
						(activityCode: SolunoActivityCode) => {
							activityCode.GLAccount = glAccount;
						},
					);
					return glAccount;
				});

				this.glAccountFileName = event.name;

				this.updateGLAccountDropdownOptions();

				this.cdr.detectChanges();
			});
	}

	updateGLAccountDropdownOptions() {
		this.glAccountsDropdownOptions = this.glAccounts.map(
			(glAccount: SolunoGLAccount) => {
				return {
					label: `${glAccount.id} - ${glAccount.accountDescription}`,
					value: glAccount,
				};
			},
		);
	}

	onSubmit(form: NgForm) {
		if (form.invalid) {
			return;
		}

		this.businessDispatch.SetSolunoActivityCodes(
			this.activityCodes,
			this.activityCodeFileName,
		);
		this.businessDispatch.SetSolunoGLAccounts(
			this.glAccounts,
			this.glAccountFileName,
		);

		this.matdialogRef.close();
	}

	trackById(index: number, item: SolunoActivityCode) {
		return item.id;
	}

	openActivityCodeUploadMenu() {
		this.analyticsService.logEvent(
			AnalyticsEvent.SOLUNO_BUSINESS_SETTINGS_REIMPORT_ACTIVITY_CODES,
		);
		this.activityCodeFileInput.openFileUploadMenu();
	}

	openGLAAccountUploadMenu() {
		this.analyticsService.logEvent(
			AnalyticsEvent.SOLUNO_BUSINESS_SETTINGS_REIMPORT_GL_CODES,
		);
		this.glAccountFileInput.openFileUploadMenu();
	}
}
